import { useState } from "react";
import {
  Box, Grid, TextField, Button, Typography, MenuItem, Select,
  FormControl, InputLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    serviceType: "",
    budget: "",
    deadline: "",
    referral: "",
    projectDetails: "",
  });

  const navigate = useNavigate();  // Initialize navigate

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If already submitting, do nothing
    if (isSubmitting) return;

    // Set submitting state to true
    setIsSubmitting(true);

    try {
      const response = await fetch("https://nicole-backend.onrender.com/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      // Log the full response for debugging
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      // Check if response is ok before parsing
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('Parsed data:', data);

      if (data.success) {
        // Reset form data
        setFormData({
          name: "",
          email: "",
          phone: "",
          address: "",
          serviceType: "",
          budget: "",
          deadline: "",
          referral: "", 
          projectDetails: "",
        });

        // Navigate to thank you page
        navigate("/thankyou");
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Full error details:", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
    } finally {
      // Reset the submitting state regardless of success or failure
      setIsSubmitting(false);
    }
  };


  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#e6e4df",
        paddingBottom: "5rem",
      }}
    >
      {/* Full-Width Image */}
      <Box
        component="img"
        src="/livingMain.jpg" // Replace with your image path
        alt="Contact Us"
        sx={{
          width: "100%",
          height: { xs: "250px", md: "800px" },
          objectFit: "cover",
        }}
      />

      {/* Content Section */}
      <Grid
        container
        spacing={4}
        sx={{
          width: "90%",
          margin: "4rem auto",
        }}
      >
        {/* Left Column - Text */}
        <Grid item xs={12} md={6} sx={{ padding: "1rem" }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: "700", marginBottom: "1rem", textAlign: { xs: "center", md: "left" } }}
          >
            We look forward to hearing from you!
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: "18px", maxWidth: "500px", textAlign: { xs: "center", md: "left" }, margin: "0 auto" }}
          >
            To find out more about our design services and to get your discovery call scheduled, please fill out the contact form.<br /><br />
            If you have any other questions or comments, please connect with Nicole Hahn via e-mail or phone.
          </Typography>
        </Grid>

        {/* Right Column - Contact Form */}
        <Grid item xs={12} md={6} sx={{ padding: "1rem" }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "2rem", md: "1.2rem" }, // More spacing on mobile
              backgroundColor: "#e6e4df",
              padding: { xs: "1.5rem", md: "2rem" },
              borderRadius: "8px",
              maxWidth: "100%",
            }}
          >
            {/* Text Fields */}
            {[
              { label: "Full Name", name: "name" },
              { label: "Email", name: "email", type: "email" },
              { label: "Phone Number", name: "phone" },
              { label: "Project Address", name: "address" },
            ].map((field, index) => (
              <TextField
                key={index}
                label={field.label}
                name={field.name}
                type={field.type || "text"}
                value={formData[field.name]}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                  style: { whiteSpace: 'normal' }
                }}
                sx={{
                  backgroundColor: "#F5F5F5",
                  borderRadius: "6px",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#999",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    lineHeight: "1.2",
                    whiteSpace: "normal",
                    maxWidth: "100%"
                  }
                }}
              />
            ))}

            {/* Dropdowns */}
            {[
              {
                label: "Which service best describes what you are looking for?",
                name: "serviceType",
                options: [
                  "Decor, Furniture, Fixtures, and/or Lighting Selections",
                  "Full Service or Remodels",
                  "Design to implement on your own",
                  "Two Hour Design or Color Consultation",
                  "Other",
                ],
              },
              {
                label: "Project Budget (not including design fees)",
                name: "budget",
                options: [
                  "$30k - $50k",
                  "$50k - $100k",
                  "$100k - $200k",
                  "$200k - $500k",
                  "$500k+",
                  "$10,000 - I am interested in Design Day",
                  "Other - I am interested in a consultation or styling",
                ],
              },
              {
                label: "How did you hear about Nicole Hahn Designs?",
                name: "referral",
                options: ["Google", "Facebook", "Instagram", "Client Referral", "Industry Referral", "Other"],
              },
            ].map((dropdown, index) => (
              <FormControl fullWidth key={index} sx={{
                backgroundColor: "#e6e4df",
                borderRadius: "6px",
                "& .MuiInputLabel-root": {
                  lineHeight: "1.2",
                  whiteSpace: "normal",
                  maxWidth: "100%"
                }
              }}>
                <InputLabel
                  sx={{
                    whiteSpace: "normal",
                    position: "relative",
                    transform: "none",
                    marginBottom: "8px"
                  }}
                >
                  {dropdown.label}
                </InputLabel>
                <Select
                  name={dropdown.name}
                  value={formData[dropdown.name]}
                  onChange={handleChange}
                  label={dropdown.label}
                  sx={{
                    backgroundColor: "#F5F5F5",
                    borderRadius: "6px",
                    marginTop: "16px"
                  }}
                >
                  {dropdown.options.map((option, idx) => (
                    <MenuItem key={idx} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}

            {/* Date Picker */}
            <FormControl fullWidth sx={{
              backgroundColor: "#e6e4df",
              borderRadius: "6px",
              "& .MuiInputLabel-root": {
                lineHeight: "1.2",
                whiteSpace: "normal",
                maxWidth: "100%"
              }
            }}>
              <InputLabel
                sx={{
                  whiteSpace: "normal",
                  position: "relative",
                  transform: "none",
                  marginBottom: "8px"
                }}
              >
                Do you have a hard deadline for the completion of your project?
              </InputLabel>
              <TextField
                name="deadline"
                type="date"
                value={formData.deadline}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: "#F5F5F5",
                  borderRadius: "6px",
                  marginTop: "16px",
                  "& .MuiInputLabel-root": {
                    display: "none" // Hide the default label
                  }
                }}
              />
            </FormControl>

            {/* Date Picker */}
            

            {/* Textarea for Additional Project Details */}
            <TextField
              label="Tell us a bit more about your project"
              name="projectDetails"
              value={formData.projectDetails}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              required
              sx={{
                backgroundColor: "#F5F5F5",
                borderRadius: "6px",
                
              }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{
                fontSize: "16px",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: isSubmitting ? "#999" : "#333",
                "&:hover": { backgroundColor: isSubmitting ? "#999" : "#222" },
              }}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>

  );
};

export default Contact;
